import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const COLORS = [
  { key: "indigo", cls: "bg-blue-500" },
  { key: "violet", cls: "bg-slate-600" },
  { key: "pink", cls: "bg-sky-500" },
  { key: "emerald", cls: "bg-emerald-500" },
  { key: "amber", cls: "bg-amber-500" },
  { key: "sky", cls: "bg-sky-500" },
  { key: "rose", cls: "bg-rose-500" },
];

export default function CreateWorkspaceDialog({ open, onOpenChange }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("indigo");
  const qc = useQueryClient();

  const m = useMutation({
    mutationFn: async () => (await api.post("/workspaces", { name, description, color })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      toast.success("Workspace creado");
      setName(""); setDescription(""); setColor("indigo");
      onOpenChange(false);
    },
    onError: () => toast.error("No se pudo crear el workspace"),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Debes ingresar un nombre");
    m.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-panel border-white/40 dark:border-white/10 sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Nuevo workspace</DialogTitle>
          <DialogDescription>Organiza tus boards en un espacio compartido.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Nombre</label>
            <Input
              autoFocus value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Mi equipo" data-testid="create-workspace-name"
              className="mt-1 bg-white/60 dark:bg-slate-900/40"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Descripción (opcional)</label>
            <Textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="¿De qué trata este workspace?" data-testid="create-workspace-description"
              className="mt-1 bg-white/60 dark:bg-slate-900/40"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Color</label>
            <div className="mt-2 flex gap-2">
              {COLORS.map((c) => (
                <button
                  type="button" key={c.key} onClick={() => setColor(c.key)}
                  data-testid={`create-workspace-color-${c.key}`}
                  className={cn("h-7 w-7 rounded-full transition", c.cls, color === c.key && "ring-2 ring-offset-2 ring-offset-transparent ring-blue-500")}
                  aria-label={c.key}
                />
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="create-workspace-cancel">Cancelar</Button>
            <Button type="submit" disabled={m.isPending} data-testid="create-workspace-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
              {m.isPending ? "Creando…" : "Crear workspace"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
